import { ExpandLess, ExpandMore, Menu as MenuIcon } from "@mui/icons-material";
import {
  Box,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer as MuiDrawer,
  Tooltip,
  useMediaQuery
} from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import { useTranslation } from "_metronic/i18n/language";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Brand from "../brand/Brand";
import { menuConfig } from "../core/";
import { ASIDE_COLLAPSED_WIDTH, ASIDE_WIDTH } from "./layoutConstants";
import IconComponent from "./renderIcons";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 64,
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar
}));

const openedMixin = theme => ({
  width: ASIDE_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: "hidden"
});

const closedMixin = theme => ({
  width: ASIDE_COLLAPSED_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: "hidden"
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== "open"
})(({ theme, open }) => ({
  width: open ? ASIDE_WIDTH : ASIDE_COLLAPSED_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open
    ? {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme)
      }
    : {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme)
      })
}));

const normalizePath = path => `/${String(path || "").replace(/^\/+|\/+$/g, "")}`;

const AsideLeft = ({ open, onToggle }) => {
  const theme = useTheme();
  const translate = useTranslation();
  const { pathname } = useLocation();
  const [submenuOpen, setSubmenuOpen] = useState("");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const showLabels = isMobile || open;
  const activeColor = theme.palette.mode === "light" ? "#1A73E8" : theme.palette.primary.main;
  const menu = useMemo(() => menuConfig(translate), [translate]);
  const activePath = useMemo(() => {
    const pages = menu.aside.items.reduce(
      (result, item) => [
        ...result,
        item.page,
        ...(item.submenu?.map(subitem => subitem.page) || [])
      ],
      []
    );

    return pages
      .filter(Boolean)
      .map(normalizePath)
      .filter(page => pathname === page || pathname.startsWith(`${page}/`))
      .sort((first, second) => second.length - first.length)[0];
  }, [menu, pathname]);

  const isActive = page => Boolean(page && normalizePath(page) === activePath);

  useEffect(() => {
    const activeParent = menu.aside.items.find(item =>
      item.submenu?.some(subitem => normalizePath(subitem.page) === activePath)
    );

    if (activeParent) {
      setSubmenuOpen(activeParent.iconMui || activeParent.title);
    }
  }, [activePath, menu]);

  const closeAfterNavigation = () => {
    if (isMobile) onToggle(false);
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <DrawerHeader>
        {showLabels ? (
          <Brand />
        ) : (
          <MenuIcon sx={{ color: "text.secondary" }} aria-hidden="true" />
        )}
      </DrawerHeader>

      <Divider sx={{ borderColor: alpha(theme.palette.text.primary, 0.2) }} />

      <List
        component="nav"
        aria-label="Menu principal"
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          px: 1,
          py: 1,
          scrollbarWidth: "thin"
        }}
      >
        {menu.aside.items.map(item => {
          const itemKey = item.iconMui || item.page || item.title;
          const hasSubmenu = Boolean(item.submenu?.length);
          const parentActive = hasSubmenu
            ? item.submenu.some(subitem => isActive(subitem.page))
            : isActive(item.page);
          const isExpanded = submenuOpen === itemKey;

          const button = (
            <ListItemButton
              component={hasSubmenu ? "button" : Link}
              to={hasSubmenu ? undefined : normalizePath(item.page)}
              onClick={() => {
                if (hasSubmenu) {
                  setSubmenuOpen(current => (current === itemKey ? "" : itemKey));
                } else {
                  closeAfterNavigation();
                }
              }}
              selected={parentActive}
              aria-expanded={hasSubmenu ? isExpanded : undefined}
              sx={{
                minHeight: 44,
                justifyContent: showLabels ? "initial" : "center",
                px: showLabels ? 1.5 : 1,
                borderRadius: 2,
                color: parentActive ? activeColor : "text.secondary",
                backgroundColor: alpha(theme.palette.text.primary, 0.055),
                transition: theme.transitions.create(["background-color", "color"]),
                "&.Mui-selected": {
                  color: activeColor,
                  backgroundColor: alpha(activeColor, 0.14),
                  boxShadow: `inset 3px 0 0 ${activeColor}`,
                  "&:hover": {
                    backgroundColor: alpha(activeColor, 0.18)
                  }
                },
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: alpha(theme.palette.text.primary, 0.11)
                }
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: showLabels ? 42 : 0,
                  justifyContent: "center",
                  color: "inherit"
                }}
              >
                <IconComponent iconName={item.iconMui} />
              </ListItemIcon>

              <ListItemText
                primary={item.title}
                primaryTypographyProps={{ fontSize: 14, fontWeight: parentActive ? 600 : 500 }}
                sx={{ opacity: showLabels ? 1 : 0, my: 0 }}
              />

              {hasSubmenu && showLabels && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
          );

          return (
            <Box key={itemKey} sx={{ mb: 0.25 }}>
              <ListItem disablePadding>
                <Tooltip
                  title={showLabels ? "" : item.title}
                  placement="right"
                  arrow
                  disableInteractive
                >
                  {button}
                </Tooltip>
              </ListItem>

              {hasSubmenu && (
                <Collapse in={showLabels && isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ mt: 0.25, mb: 0.5 }}>
                    {item.submenu.map(subitem => {
                      const subitemActive = isActive(subitem.page);

                      return (
                        <ListItem key={subitem.page || subitem.title} disablePadding>
                          <ListItemButton
                            component={Link}
                            to={normalizePath(subitem.page)}
                            selected={subitemActive}
                            onClick={closeAfterNavigation}
                            sx={{
                              minHeight: 38,
                              pl: 3.25,
                              pr: 1.5,
                              mb: 0.25,
                              borderRadius: 2,
                              color: subitemActive ? activeColor : "text.secondary",
                              backgroundColor: alpha(theme.palette.text.primary, 0.035),
                              "&.Mui-selected": {
                                backgroundColor: alpha(activeColor, 0.11),
                                boxShadow: `inset 3px 0 0 ${activeColor}`
                              },
                              "&:hover": {
                                color: "text.primary",
                                backgroundColor: alpha(theme.palette.text.primary, 0.08)
                              }
                            }}
                          >
                            <Box
                              aria-hidden="true"
                              sx={{
                                width: 5,
                                height: 5,
                                mr: 1.5,
                                flexShrink: 0,
                                borderRadius: "50%",
                                bgcolor: subitemActive ? activeColor : "text.disabled"
                              }}
                            />
                            <ListItemText
                              primary={subitem.title}
                              primaryTypographyProps={{
                                fontSize: 13,
                                fontWeight: subitemActive ? 600 : 400
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>
    </Box>
  );

  if (isMobile) {
    return (
      <MuiDrawer
        variant="temporary"
        open={open}
        onClose={() => onToggle(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: ASIDE_WIDTH,
            maxWidth: "calc(100vw - 32px)",
            overflowX: "hidden",
            borderRight: 0
          }
        }}
      >
        {drawerContent}
      </MuiDrawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      open={open}
      onMouseEnter={() => onToggle(true)}
      onMouseLeave={() => onToggle(false)}
      PaperProps={{
        sx: {
          borderRightColor: alpha(theme.palette.divider, 0.7),
          boxShadow: open ? `8px 0 24px ${alpha(theme.palette.common.black, 0.06)}` : "none"
        }
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default AsideLeft;
